import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { getDeviceHint } from "../../helpers/getDeviceHint";
import moment from "moment";

// Single-flight refresh guard: vive fuera del componente porque debe ser
// compartido por TODAS las instancias del interceptor (cada mount de un
// componente que usa useAuth) y por invocaciones concurrentes dentro de la
// misma instancia -- varios 403 simultáneos deben esperar la MISMA llamada a
// /auth/refresh_token en vez de disparar una por cada request fallida.
let refreshTokenPromise = null;

const requestRefreshToken = () => {
  if (!refreshTokenPromise) {
    const deviceHint = getDeviceHint();
    refreshTokenPromise = api
      .post("/auth/refresh_token", null, {
        headers: deviceHint ? { "X-Device-Hint": deviceHint } : {},
      })
      .then(({ data }) => data)
      .finally(() => {
        // Limpiar siempre (éxito o rechazo) para que el próximo 403
        // genuino dispare un refresh nuevo en vez de quedar colgado
        // esperando una promise ya resuelta/rechazada.
        refreshTokenPromise = null;
      });
  }
  return refreshTokenPromise;
};

const useAuth = () => {
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);

  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  // Los interceptors solo leen del localStorage/módulo en el momento de la
  // llamada (no cierran sobre `user` ni ningún estado que cambie) y sus
  // únicas referencias a estado son los setters de useState, que React
  // garantiza estables entre renders -- por eso alcanza con registrarlos
  // UNA vez por mount (deps []) en vez de en cada render. Antes se
  // registraban directo en el cuerpo del hook, sin eject, así que cada
  // mount de cualquier componente que llama useAuth() apilaba un par más
  // de interceptors sobre la instancia compartida de axios.
  useEffect(() => {
    const responseInterceptorId = api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        if (error?.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Ver TAREA: sesión no se registraba en refresh -- sin el mismo
          // X-Device-Hint que se manda en /auth/login, un iPad (mismo
          // user-agent que macOS desktop) se reclasifica de deviceType en
          // cada refresh y el backend no encuentra la fila de Session que
          // corresponde actualizar. Mismo getDeviceHint() ya importado
          // arriba, no se recalcula distinto.
          try {
            const data = await requestRefreshToken();
            if (data) {
              localStorage.setItem("token", JSON.stringify(data.token));
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
            }
            return api(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        if (error?.response?.status === 401) {
          const reqUrl = originalRequest?.url || "";

          // /auth/login: credenciales malas, no hay token que borrar.
          if (/\/auth\/login(\?|$)/.test(reqUrl)) {
            return Promise.reject(error);
          }

          // /auth/refresh_token: sesion muerta (cookie jrt ausente/vencida).
          // Siempre cierra sesion, sin comparar headers.
          const isRefreshCall = /\/auth\/refresh_token(\?|$)/.test(reqUrl);

          let currentHeader = null;
          try {
            const raw = localStorage.getItem("token");
            if (raw) currentHeader = `Bearer ${JSON.parse(raw)}`;
          } catch (e) {}
          const sentHeader = originalRequest?.headers?.Authorization || null;

          const wipe = () => {
            localStorage.removeItem("token");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
          };

          if (isRefreshCall) {
            wipe();
            return Promise.reject(error);
          }

          if (!currentHeader && !sentHeader) {
            // Nada mas nuevo que el token malo: comportamiento de siempre.
            wipe();
          } else if (!currentHeader && sentHeader) {
            // Otra pestana ya cerro sesion: solo reflejarlo, sin borrar nada.
            setIsAuth(false);
          } else if (sentHeader === currentHeader) {
            // El token vigente fue el rechazado: realmente es invalido.
            wipe();
          } else {
            // 401 de un request viejo/temprano (salio sin token o con uno
            // anterior) mientras ya existe un token vigente: no cerrar sesion.
            const method = String(originalRequest?.method || "get").toLowerCase();
            const safe = method === "get" || method === "head" || method === "options";
            if (safe && originalRequest && !originalRequest._retry401) {
              originalRequest._retry401 = true;
              if (originalRequest.headers) {
                delete originalRequest.headers.Authorization;
              }
              // Re-entra al request interceptor, que inyecta el token vigente.
              return api(originalRequest);
            }
            // No idempotente (o ya reintentado): rechazar sin borrar ni reintentar.
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  // Effect para inicialização do token
  useEffect(() => {
    // SSO bajo demanda — acepta #token=xxx enviado desde Agenda (Citas).
    // Se usa el fragment (no query param) para que el token nunca se envíe
    // al servidor ni quede en logs de acceso.
    let hash = "";
    try { hash = window.location.hash; } catch(e) {}
    const ssoToken = hash.startsWith("#token=") ? decodeURIComponent(hash.slice("#token=".length)) : null;

    if (ssoToken) {
      // Este token llegó por URL (login hecho en Agenda), no por nuestro
      // propio /auth/login — por lo tanto esta sesión NO tiene la cookie
      // jrt de refresh. Llamar a /auth/refresh_token aquí fallaría con 401
      // y el interceptor global borraría el token recién guardado. En vez
      // de eso, se usa el token directo como Bearer y se pide el perfil
      // por API (que solo depende del header, no de la cookie).
      try {
        window.history.replaceState({}, "", window.location.pathname + window.location.search);
      } catch(e) {}
      (async () => {
        const existingToken = localStorage.getItem("token");
        if (!existingToken) {
          try {
            const payload = JSON.parse(atob(ssoToken.split(".")[1]));
            api.defaults.headers.Authorization = `Bearer ${ssoToken}`;
            const { data } = await api.get(`/users/${payload.id}`);
            localStorage.setItem("token", JSON.stringify(ssoToken));
            setUser(data);
            setIsAuth(true);
          } catch (err) {
            api.defaults.headers.Authorization = undefined;
          }
        }
        setLoading(false);
      })();
      return;
    }

    // Si estamos dentro de un iframe SSO, no hacer llamadas API
    // (evita que el refresh_token borre el token del parent window)
    if (window !== window.top) { setLoading(false); return; }
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          // Ver TAREA: mismo motivo que el interceptor de arriba -- el
          // X-Device-Hint tiene que viajar en TODO refresh, no solo en
          // el login. Usa el mismo guard single-flight que el interceptor
          // 403 para que un refresh de montaje y un refresh disparado por
          // un 403 casi simultáneo (carrera típica al cargar la página)
          // no disparen dos llamadas a /auth/refresh_token.
          const data = await requestRefreshToken();
          // Persistir tambien en localStorage: el request interceptor
          // reescribe Authorization desde localStorage en cada request,
          // asi que sin esto la pagina seguia enviando el token del login.
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Fase 2: si este dispositivo/pestaña fue expulsado por un nuevo
          // login del mismo tipo de dispositivo, cerrar sesión localmente.
          const handleSessionReplaced = () => {
            toast.warn(i18n.t("auth.toasts.sessionReplaced", { defaultValue: "Se cerró tu sesión porque iniciaste sesión en otro dispositivo" }));
            localStorage.removeItem("token");
            localStorage.removeItem("cshow");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
            setUser({});
            if (socketInstance && typeof socketInstance.disconnect === "function") {
              socketInstance.disconnect();
            }
            setSocket(null);
            window.location.href = "/login";
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);

            socketInstance.on("session:replaced", handleSessionReplaced);
            listenersRef.current.add("session:replaced");
          }

        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas


  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const deviceHint = getDeviceHint();
      const { data } = await api.post("/auth/login", userData, {
        headers: deviceHint ? { "X-Device-Hint": deviceHint } : {},
      });
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        navigate("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        navigate("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (idToken) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/google", { idToken });
      const {
        user: { company },
      } = data;

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }

      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }

      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      const hojeInicio = moment().startOf("day");
      const vencimentoInicio = moment(dueDate).startOf("day");

      var diff = vencimentoInicio.diff(hojeInicio, "days");
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, "day");
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        navigate("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        navigate("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  // Registro con Google: crea Company + User admin nuevos (RFC/plan quedan
  // pendientes, profileIncomplete=true) y deja la sesión iniciada de una vez.
  const handleSignupGoogle = async (idToken) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/signup-google", { idToken });

      localStorage.setItem("profileImage", data.user.profileImage);
      localStorage.setItem("token", JSON.stringify(data.token));
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user || data);
      setIsAuth(true);
      toast.success(i18n.t("auth.toasts.success"));
      navigate("/onboarding");
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      // Logout independiente: no propaga el cierre de sesión a Agenda.
      window.location.href = "/login";
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleGoogleLogin,
    handleSignupGoogle,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;